import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { signOut } from 'firebase/auth';
import { auth } from '@/api/firebase';
import { useAuthStore } from '@/app/store/auth.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { RolePicker } from '@/components/forms/RolePicker';

import { useAssignableRoles } from '../hooks/use-assignable-roles';
import { useRootTransfer } from '../hooks/use-admin-mutations';
import { useRootTransferCandidatesQuery } from '../hooks/use-admins';
import { AdminApiError, type Admin } from '../types/admin.types';
import { getAdminErrorMessage } from '../utils/error-message';
import { formatLastLogin } from '../utils/format-date';

interface RootTransferPanelProps {
  open: boolean;
  onClose: () => void;
}

// PRD 15 §3.5: 3-stage flow.
// Stage 1 = select target + roles-to-keep, 2 = confirm summary, 3 = password re-entry.
type Stage = 1 | 2 | 3;

// 5-second pause matches prototype admin-managers.html (allows user to read the toast).
const FORCE_LOGOUT_DELAY_MS = 5000;

export function RootTransferPanel({ open, onClose }: RootTransferPanelProps) {
  const { t } = useTranslation('admin');
  const currentEmail = useAuthStore((s) => s.email);
  const candidatesQuery = useRootTransferCandidatesQuery(open);
  const assignableRoles = useAssignableRoles();
  const transferMutation = useRootTransfer();

  const [stage, setStage] = useState<Stage>(1);
  // v9: target admin id is a number (BIGINT UNSIGNED per DEC-DB-PK-BIGINT-001).
  const [targetId, setTargetId] = useState<number | null>(null);
  const [keepRoles, setKeepRoles] = useState<string[]>([]);
  const [password, setPassword] = useState('');
  const [attemptsFailed, setAttemptsFailed] = useState(0);
  const [targetError, setTargetError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  // Captured when user clicks Next from stage 1 (per PRD §3.5.3 "transfer time").
  const [transferTime, setTransferTime] = useState('');
  const [showDiscard, setShowDiscard] = useState(false);
  const [showRootTransferModal, setShowRootTransferModal] = useState(false);

  // Reset everything on each open transition. attemptsFailed resets too — the
  // mock + backend keep a server-side counter that is the source of truth.
  useEffect(() => {
    if (open) {
      setStage(1);
      setTargetId(null);
      setKeepRoles([]);
      setPassword('');
      setAttemptsFailed(0);
      setTargetError('');
      setPasswordError('');
      setTransferTime('');
      setShowDiscard(false);
      setShowRootTransferModal(false);
    }
  }, [open]);

  const candidates = candidatesQuery.data ?? [];
  const target: Admin | null = targetId != null
    ? candidates.find((c) => c.id === targetId) ?? null
    : null;

  // Dirty once the user has invested any input.
  const isDirty =
    targetId !== null || keepRoles.length > 0 || password.length > 0;

  const handleClose = () => {
    if (transferMutation.isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  };

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    onClose();
  };

  const goToStage2 = () => {
    if (targetId === null) {
      setTargetError(t('adminManagement.rootTransfer.stage1.errorTargetRequired'));
      return;
    }
    setTargetError('');
    setTransferTime(formatLastLogin(new Date().toISOString()));
    setStage(2);
  };

  const handleExecute = async () => {
    if (transferMutation.isPending) return;
    if (!targetId) return;
    if (!password) {
      setPasswordError(t('adminManagement.rootTransfer.stage3.passwordRequired'));
      return;
    }
    setPasswordError('');
    try {
      const result = await transferMutation.mutateAsync({
        targetAdminId: targetId,
        keepRoles,
        password,
      });
      onClose();
      toast.success(
        t('adminManagement.toasts.rootTransferSuccess', {
          name: result.newRoot.name,
        }),
      );
      // Force-logout simulation — backend invalidates the session, but FE also
      // clears auth state after the user has had a chance to read the toast.
      window.setTimeout(() => {
        void signOut(auth);
        useAuthStore.getState().logout();
      }, FORCE_LOGOUT_DELAY_MS);
    } catch (e) {
      if (e instanceof AdminApiError) {
        if (e.code === 'PASSWORD_INVALID') {
          const attemptsLeft =
            typeof e.meta?.attemptsLeft === 'number' ? e.meta.attemptsLeft : 0;
          setAttemptsFailed(3 - attemptsLeft);
          setPasswordError(e.message);
          return;
        }
        if (e.code === 'LOCKED_5MIN') {
          setShowRootTransferModal(true);
          return;
        }
        if (e.code === 'NO_CANDIDATES' || e.code === 'TARGET_INVALID') {
          onClose();
          toast.error(getAdminErrorMessage(e, t, 'adminManagement.toasts.rootTransferError'));
          return;
        }
        // Catch-all: an unmodelled code reaches here as UNKNOWN, so the helper returns the
        // 이양 실패 copy instead of the server's raw text (SHCT-685).
        onClose();
        toast.error(getAdminErrorMessage(e, t, 'adminManagement.toasts.rootTransferError'));
        return;
      }
      toast.error(t('adminManagement.toasts.rootTransferError'));
    }
  };

  return (
    <>
      <SlidePanel
        open={open}
        title={t('adminManagement.rootTransfer.title')}
        width={560}
        onClose={handleClose}
      >
        {candidatesQuery.isLoading ? (
          <p style={{ color: 'var(--muted-foreground)' }}>
            {t('adminManagement.rootTransfer.loading')}
          </p>
        ) : candidates.length === 0 ? (
          <NoCandidates onClose={handleClose} />
        ) : stage === 1 ? (
          <Stage1
            candidates={candidates}
            assignableRoles={assignableRoles}
            currentEmail={currentEmail}
            targetId={targetId}
            keepRoles={keepRoles}
            targetError={targetError}
            onSelectTarget={(id) => {
              setTargetId(id);
              if (targetError) setTargetError('');
            }}
            onKeepRolesChange={setKeepRoles}
            onCancel={handleClose}
            onNext={goToStage2}
          />
        ) : stage === 2 ? (
          <Stage2
            target={target}
            currentEmail={currentEmail}
            transferTime={transferTime}
            keepRoles={keepRoles}
            onBack={() => setStage(1)}
            onNext={() => setStage(3)}
          />
        ) : (
          <Stage3
            attemptsFailed={attemptsFailed}
            password={password}
            passwordError={passwordError}
            isExecuting={transferMutation.isPending}
            onPasswordChange={(v) => {
              setPassword(v);
              if (passwordError) setPasswordError('');
            }}
            onBack={() => setStage(2)}
            onExecute={handleExecute}
          />
        )}
      </SlidePanel>

      <ConfirmModal
        open={showDiscard}
        title={t('adminManagement.invitePanel.discardTitle')}
        message={t('adminManagement.invitePanel.discardMessage')}
        confirmLabel={t('adminManagement.modals.discard.confirm')}
        cancelLabel={t('adminManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscard(false)}
      />

      <InfoModal
        open={showRootTransferModal}
        title={t('adminManagement.rootTransferModal.title')}
        body={t('adminManagement.toasts.rootTransferLocked')}
        confirmLabel={t('adminManagement.rootTransferModal.confirm')}
        onClose={() => {
          setShowRootTransferModal(false);
          onClose();
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage-specific sub-views (kept in this file — flow is a single feature unit).
// ---------------------------------------------------------------------------

function NoCandidates({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <>
      <div
        style={{
          padding: 'var(--space-4)',
          background: '#fef3c7',
          borderLeft: '4px solid var(--warning)',
          borderRadius: 'var(--radius-sm)',
          color: '#92400e',
        }}
      >
        {t('adminManagement.rootTransfer.noCandidates')}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 'var(--space-4)',
        }}
      >
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={onClose}
        >
          {t('adminManagement.rootTransfer.close')}
        </button>
      </div>
    </>
  );
}

interface Stage1Props {
  candidates: Admin[];
  assignableRoles: readonly string[];
  currentEmail: string;
  targetId: number | null;
  keepRoles: string[];
  targetError: string;
  onSelectTarget: (id: number) => void;
  onKeepRolesChange: (roles: string[]) => void;
  onCancel: () => void;
  onNext: () => void;
}

function Stage1({
  candidates,
  assignableRoles,
  currentEmail,
  targetId,
  keepRoles,
  targetError,
  onSelectTarget,
  onKeepRolesChange,
  onCancel,
  onNext,
}: Stage1Props) {
  const { t } = useTranslation('admin');

  return (
    <>
      <div
        style={{
          padding: 12,
          background: '#fee2e2',
          borderLeft: '4px solid var(--destructive)',
          borderRadius: 'var(--radius-sm)',
          color: '#991b1b',
          marginBottom: 'var(--space-4)',
        }}
      >
        {t('adminManagement.rootTransfer.warning', { email: currentEmail })}
      </div>

      <div className="form-field">
        <label className="form-field__label">
          {t('adminManagement.rootTransfer.stage1.targetLabel')}{' '}
          <span className="required">*</span>
        </label>
        <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '0 0 8px 0' }}>
          {t('adminManagement.rootTransfer.stage1.targetHelp')}
        </p>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {candidates.map((c) => {
            const checked = targetId === c.id;
            return (
              <label
                key={c.id}
                style={{
                  display: 'block',
                  padding: '8px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: 4,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="xferTarget"
                  checked={checked}
                  onChange={() => onSelectTarget(c.id)}
                  style={{ marginRight: 8 }}
                />
                <strong>{c.name}</strong>{' '}
                <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>
                  ({c.email})
                </span>{' '}
                <span
                  className="badge badge-outline"
                  style={{ fontSize: 10, marginLeft: 6 }}
                >
                  {c.roles.length > 0
                    ? c.roles.join('+')
                    : t('adminManagement.rootTransfer.stage2.noRoles')}
                </span>
              </label>
            );
          })}
        </div>
        {targetError && <p className="form-field__error">{targetError}</p>}
      </div>

      <div className="form-field">
        <label className="form-field__label">
          {t('adminManagement.rootTransfer.stage1.keepRolesLabel')}
        </label>
        <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '0 0 8px 0' }}>
          {t('adminManagement.rootTransfer.stage1.keepRolesHelp')}
        </p>
        {/* SHCT-523 / [ADM.062] — single-select plus an explicit 「선택 안 함」. PRD 15 v2.4
            §3.5.2 reads "1개 또는 「선택 안 함」": at most one Role, and keeping none stays a
            reachable outcome instead of being the accidental result of clearing every box. */}
        <RolePicker
          options={assignableRoles}
          value={keepRoles}
          onChange={onKeepRolesChange}
          mode="single"
          allowNone
          noneLabel={t('adminManagement.rootTransfer.stage1.keepNone')}
          idPrefix="xfer-keep"
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-4)',
        }}
      >
        <button type="button" className="btn btn-default btn-secondary" onClick={onCancel}>
          {t('adminManagement.rootTransfer.cancel')}
        </button>
        <button type="button" className="btn btn-default btn-primary" onClick={onNext}>
          {t('adminManagement.rootTransfer.stage1.next')}
        </button>
      </div>
    </>
  );
}

interface Stage2Props {
  target: Admin | null;
  currentEmail: string;
  transferTime: string;
  keepRoles: string[];
  onBack: () => void;
  onNext: () => void;
}

function Stage2({
  target,
  currentEmail,
  transferTime,
  keepRoles,
  onBack,
  onNext,
}: Stage2Props) {
  const { t } = useTranslation('admin');
  if (!target) return null;

  const keepRolesStr =
    keepRoles.length > 0
      ? keepRoles.join(', ')
      : t('adminManagement.rootTransfer.stage2.noRoles');

  return (
    <>
      <div
        style={{
          padding: 'var(--space-4)',
          background: 'var(--muted)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 4 }}>
          {t('adminManagement.rootTransfer.stage2.heading')}
        </div>
        <div style={{ lineHeight: 1.8 }}>
          <div>
            <strong>{t('adminManagement.rootTransfer.stage2.targetLine')}</strong>:{' '}
            {target.name} ({target.email})
          </div>
          <div>
            <strong>{t('adminManagement.rootTransfer.stage2.timeLine')}</strong>:{' '}
            {transferTime} {t('adminManagement.rootTransfer.stage2.timeNote')}
          </div>
        </div>
      </div>

      <div
        style={{
          padding: 'var(--space-4)',
          background: '#f0f9ff',
          borderLeft: '4px solid var(--info, #0284c7)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--space-4)',
          lineHeight: 1.8,
        }}
      >
        <div>
          <strong>
            {t('adminManagement.rootTransfer.stage2.keepRolesLine', { email: currentEmail })}
          </strong>
          : {keepRolesStr}
        </div>
        <div>
          <strong>
            {t('adminManagement.rootTransfer.stage2.newRootLine', { name: target.name })}
          </strong>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-4)',
        }}
      >
        <button type="button" className="btn btn-default btn-secondary" onClick={onBack}>
          {t('adminManagement.rootTransfer.stage2.back')}
        </button>
        <button type="button" className="btn btn-default btn-primary" onClick={onNext}>
          {t('adminManagement.rootTransfer.stage2.next')}
        </button>
      </div>
    </>
  );
}

interface Stage3Props {
  attemptsFailed: number;
  password: string;
  passwordError: string;
  isExecuting: boolean;
  onPasswordChange: (value: string) => void;
  onBack: () => void;
  onExecute: () => void;
}

function Stage3({
  attemptsFailed,
  password,
  passwordError,
  isExecuting,
  onPasswordChange,
  onBack,
  onExecute,
}: Stage3Props) {
  const { t } = useTranslation('admin');

  return (
    <>
      <div
        style={{
          padding: 'var(--space-4)',
          background: 'var(--muted)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 4 }}>
          {t('adminManagement.rootTransfer.stage3.heading')}
        </div>
        <div style={{ lineHeight: 1.6 }}>
          {t('adminManagement.rootTransfer.stage3.summary')}{' '}
          <br />
          <strong style={{ color: 'var(--destructive)' }}>
            {t('adminManagement.rootTransfer.stage3.irreversibleWarning')}
          </strong>
        </div>
      </div>

      <div className="form-field">
        <label className="form-field__label">
          {t('adminManagement.rootTransfer.stage3.passwordLabel')}{' '}
          <span className="required">*</span>
        </label>
        <input
          type="password"
          className={`form-field__input${passwordError ? ' form-field__input--error' : ''}`}
          placeholder={t('adminManagement.rootTransfer.stage3.passwordPlaceholder')}
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          disabled={isExecuting}
        />
        {passwordError && <p className="form-field__error">{passwordError}</p>}
        <p
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            marginTop: 4,
          }}
        >
          {t('adminManagement.rootTransfer.stage3.passwordHelp', { count: attemptsFailed })}
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-4)',
        }}
      >
        <button
          type="button"
          className="btn btn-default btn-secondary"
          onClick={onBack}
          disabled={isExecuting}
        >
          {t('adminManagement.rootTransfer.stage3.back')}
        </button>
        <button
          type="button"
          className="btn btn-default btn-destructive"
          onClick={onExecute}
          disabled={isExecuting}
        >
          {isExecuting
            ? t('adminManagement.rootTransfer.stage3.executing')
            : t('adminManagement.rootTransfer.stage3.execute')}
        </button>
      </div>
    </>
  );
}
